import type { ValidatedDividend } from '@porttang/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth';
import { createDividend, deleteRealized, listRealized } from './realizedRepo';

export function realizedQueryKey(uid: string) {
  return ['realized', uid] as const;
}

/**
 * หน้านี้อยู่หลัง RequireAuth เสมอ — throw ไว้ให้พังชัด ๆ ถ้ามีคนเอาไปใช้นอก guard
 *
 * เขียนแยกจาก `useHoldings` ไม่ import ข้ามกัน เพราะ `useHoldings` ต้อง import
 * `realizedQueryKey` จากที่นี่ (ขายแล้วต้อง invalidate ทั้งสองชุด) จะได้ไม่เป็นวงกลม
 */
function useUid(): string {
  const { user } = useAuth();
  if (user === null) {
    throw new Error('ต้องเข้าสู่ระบบก่อนจึงจะใช้ข้อมูลได้');
  }
  return user.uid;
}

export function useRealized() {
  const uid = useUid();

  return useQuery({
    queryKey: realizedQueryKey(uid),
    queryFn: () => listRealized(uid),
  });
}

export function useCreateDividend() {
  const uid = useUid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dividend: ValidatedDividend) => createDividend(uid, dividend),
    onSettled: () => queryClient.invalidateQueries({ queryKey: realizedQueryKey(uid) }),
  });
}

export function useDeleteRealized() {
  const uid = useUid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => deleteRealized(uid, entryId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: realizedQueryKey(uid) }),
  });
}
